import React, { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';

import { PermissionsGate, useFlags } from '@components';
import { usePermissions, useFavoriteDashboards } from '@hooks';

import { MenuArrow } from './MenuArrow';
import { Submenu } from './Submenu/Submenu';
import { MenuWrapper } from './MenuWrapper';

import { NAVIGATION_SECTION } from './constants';
import { renderMenuItem } from './utils';
import getRoutes from '../getRoutes';
import styles from './menu.style';

export const Menu = ({ open, onCloseMenu }) => {
  const { pathname } = useRouter();

  const { findEntity } = usePermissions();
  const { isReturnInboxEnabled } = useFlags();
  const { favoriteDashboards } = useFavoriteDashboards();

  const [expandedRoutes, setExpandedRoutes] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(
    () =>
      new Set([
        NAVIGATION_SECTION.myHub.value,
        NAVIGATION_SECTION.workspace.value,
      ])
  );

  const routes = useMemo(() => {
    const allRoutes = getRoutes(findEntity, isReturnInboxEnabled);
    return allRoutes;
  }, [findEntity, isReturnInboxEnabled]);

  const myHubRoutes = useMemo(
    () =>
      routes
        .filter(
          (routeItem) => routeItem.section === NAVIGATION_SECTION.myHub.value
        )
        .flatMap((routeItem) => routeItem.options) || [],
    [routes]
  );

  useEffect(() => {
    const selected = routes.find((route) => {
      return (
        (pathname.indexOf(route.url) > -1 && route.url !== '/') ||
        route.options?.some((item) => pathname === item.url)
      );
    });

    if (selected) {
      setExpandedRoutes((prev) => {
        const next = new Set(prev);
        next.add(selected.key);
        return next;
      });

      const selectedSection = selected.section;
      if (selectedSection) {
        setExpandedSections((prev) => {
          const next = new Set(prev);
          next.add(selectedSection);
          return next;
        });
      }
    }
  }, [pathname, routes]);

  useEffect(() => {
    if (!favoriteDashboards?.length) {
      return;
    }

    setExpandedSections((prevSections) => {
      if (prevSections.has(NAVIGATION_SECTION.favorites.value)) {
        return prevSections;
      }

      const newSections = new Set(prevSections);
      newSections.add(NAVIGATION_SECTION.favorites.value);
      return newSections;
    });
  }, [favoriteDashboards]);

  const handleClick = (route) => {
    setExpandedRoutes((prev) => {
      const routesSet = new Set(prev);
      if (routesSet.has(route.key)) {
        routesSet.delete(route.key);
      } else {
        routesSet.add(route.key);
      }
      return routesSet;
    });
  };

  const handleClickSection = (sectionValue) => {
    setExpandedSections((prev) => {
      const sectionSet = new Set(prev);
      if (sectionSet.has(sectionValue)) {
        sectionSet.delete(sectionValue);
      } else {
        sectionSet.add(sectionValue);
      }
      return sectionSet;
    });
  };

  return (
    <MenuWrapper>
      {Object.values(NAVIGATION_SECTION).map((sectionData) => {
        const sectionLabel = sectionData.label;

        const sectionValue = sectionData.value;

        let filteredRoutes = routes.filter(
          (route) => route.section === sectionValue
        );

        if (sectionValue === NAVIGATION_SECTION.favorites.value) {
          filteredRoutes = [...(favoriteDashboards || [])];
        }

        if (sectionValue === NAVIGATION_SECTION.myHub.value) {
          filteredRoutes = [...(myHubRoutes || [])];
        }

        const isSectionExpanded = expandedSections.has(sectionValue);

        return (
          <Box key={sectionValue}>
            <Box
              sx={styles.sectionItemsWrapper}
              onClick={() => handleClickSection(sectionValue)}
            >
              <Box sx={styles.sectionItem}>{sectionLabel}</Box>
              <MenuArrow expanded={isSectionExpanded} />
            </Box>

            {isSectionExpanded &&
              (filteredRoutes?.length > 0 ? (
                filteredRoutes.map((route) => {
                  if (route.hide) return null;

                  const Icon = route.icon;

                  const isExpandedRoute = expandedRoutes.has(route.key);

                  const isFavoriteRoute =
                    sectionValue === NAVIGATION_SECTION.favorites.value;

                  const isMyHubRoute =
                    sectionValue === NAVIGATION_SECTION.myHub.value;

                  const isExpandedFavoriteSection = expandedSections.has(
                    NAVIGATION_SECTION.favorites.value
                  );

                  const isExpandedMyHubSection = expandedSections.has(
                    NAVIGATION_SECTION.myHub.value
                  );

                  return (
                    <PermissionsGate
                      key={route?.key}
                      disabled={route?.disablePermissionGate}
                      entity={route?.entity}
                    >
                      <Box sx={styles.menuItemWrapper}>
                        {renderMenuItem(
                          isMyHubRoute,
                          isFavoriteRoute,
                          isExpandedMyHubSection,
                          isExpandedFavoriteSection,
                          isExpandedRoute,
                          myHubRoutes,
                          Icon,
                          handleClick,
                          onCloseMenu,
                          route,
                          open
                        )}
                        {route.options?.length ? (
                          <Submenu
                            route={route}
                            open={open}
                            isExpanded={isExpandedRoute}
                            onCloseMenu={onCloseMenu}
                          />
                        ) : null}
                      </Box>
                    </PermissionsGate>
                  );
                })
              ) : (
                <Box sx={styles.noSectionItems}>
                  <Box sx={styles.noItemsDummyElement} />
                  No items found
                </Box>
              ))}
          </Box>
        );
      })}
    </MenuWrapper>
  );
};

export default Menu;
